import type { ReactNode } from 'react'

import { t } from '@ctcells/i18n'

import './layout.css'

export interface PageHeaderProps {
  title: string
  /** 제목의 툴팁(`title` 속성). 제목이 사람 이름일 때 식별자를 싣는다 — 보이는 글자는 늘리지 않는다 */
  titleHint?: string | undefined
  /** 제목 옆 상태 배지. 원자 `StatusBadge` 를 그대로 넣는다(`.ahead h2 .stb` 496행) */
  badge?: ReactNode
  /**
   * 제목 «밖» · 제목 줄 오른쪽 끝(서랍이면 × 왼쪽)에 서는 것(모듈 상세의 연결 칩 · eric 2026-10-22 「연결 칩을 오른쪽으로 이동해줘」).
   * `badge` 와 달리 `h2` 안에 들지 않아 **제목의 접근성 이름이 제목 글자만**이다. 넘길 때만 제목과 한 줄 묶음(`.ahead__lead`)이 생긴다 —
   * 안 넘기면 종전 구조 그대로다
   */
  titleAside?: ReactNode
  /** 오른쪽 주 버튼 줄. 알약꼴 한 줄이다(`.ahead .btn` 509행) */
  actions?: ReactNode
  /** 목록으로 돌아가는 줄(`.back` 495행). 출처를 기억한 문구를 그대로 넣는다 */
  back?: ReactNode
  /**
   * 서랍으로 열린 화면의 닫기. 넘기면 **오른쪽 위에 ×** 가 선다(eric 2026-09-22 「닫기는 오른쪽 위에 X 로 있어야지」).
   * 돌아가기 줄(`back`)과 같이 쓰지 않는다 — 서랍은 목록 옆에 붙어 있어 돌아갈 곳이 곧 옆이다.
   */
  onClose?: (() => void) | undefined
}

/**
 * 관리형 화면 머리 — `.ahead`(CSS 429행). 목록·상세·설정형이 전부 이걸 쓴다.
 *
 * 부제·설명 문장 슬롯을 두지 않았다. 일부러다 — §1-1 「설명 문장·안내문·부연 금지」.
 * 규칙 설명이 필요하면 비활성 버튼 사유로만 내보낸다.
 */
export function PageHeader({
  title,
  titleHint,
  badge,
  titleAside,
  actions,
  back,
  onClose,
}: PageHeaderProps) {
  const heading = (
    <h2 className="ahead__title" title={titleHint}>
      {title}
      {badge}
    </h2>
  )
  return (
    <>
      {onClose === undefined ? back : null}
      <div className="ahead">
        {titleAside === undefined ? (
          heading
        ) : (
          <div className="ahead__lead">
            {heading}
            {titleAside}
          </div>
        )}
        {actions || onClose !== undefined ? (
          <div className="ahead__actions">
            {actions}
            {onClose === undefined ? null : (
              <button
                aria-label={t('action.close')}
                className="closex"
                onClick={onClose}
                title={t('action.close')}
                type="button"
              >
                <span aria-hidden="true">×</span>
              </button>
            )}
          </div>
        ) : null}
      </div>
    </>
  )
}
